import React, { PureComponent } from 'react';
import { connect, MapStateToProps, MapDispatchToProps } from 'react-redux';
import { Grid, Option, AnalyticsPage } from '@inplayer-org/inplayer-ui';
import styled from 'styled-components';
import { MdGridOn, MdFormatListBulleted } from 'react-icons/md';
import { parse } from 'query-string';
import { withRouter, RouteComponentProps } from 'react-router';

// Utils
import { transformSearchParams } from 'utils/helpers';
import { Optional } from 'utils/types';

// Actions
import {
  loadAssets as loadAssetsAction,
  reactivateAsset as reactivateAssetAction,
  resetAssetDetails as resetAssetDetailsAction,
  createItem as createItemAction,
  LoadAssetsParams,
  CreateItem,
} from 'ducks/merchantDucks/items/actions';
import { loadPackages as loadPackagesAction, LoadPackagePayload } from 'ducks/merchantDucks/packages/actions';
import { loadSalesCount as loadSalesCountAction, LoadReportsCommonParams } from 'ducks/merchantDucks/reports/actions';
import { openModal as openModalAction } from 'ducks/merchantDucks/modal/actions';

// Types
import { AuthState } from 'ducks/merchantDucks/user/types';
import ItemsState from 'ducks/merchantDucks/items/types';
import ReportsState from 'ducks/merchantDucks/reports/types';
import PackagesState from 'ducks/merchantDucks/packages/types';
import RootState from 'ducks/RootState';
import { ModalNames } from 'ducks/merchantDucks/modal/types';
import Rules from 'restrictions/roles/constants/rules';

// Components
import AssetsTable from 'pages/MerchantPages/Assets/components/AssetsTable';
import TopInformation from 'components/TopInformation';
import AssetCreateWizard from 'pages/MerchantPages/Assets/InitialCreate/StepWizard';
import ContentHolder from 'components/ContentHolder';
import ExtendedDropdown from 'components/ExtendedDropdown';
import BaseModal, { ModalActions } from 'components/Modals/BaseModal';
import RestrictedByRule from 'restrictions/roles/components/RestrictedByRule';
import Filter from 'components/Filter';
import CustomTabNavigation from 'pages/MerchantPages/Analytics/components/CustomTabNavigation';
import TourGuideWrapper from 'components/TourGuide';
import SearchTooltip from 'components/SearchTooltip';
import AssetsGrid from './AssetsGrid';
import CreateAssetButtonDropdown from '../Templates/CreateAssetButtonDropdown';

const { Container, Cell } = Grid;

export const PlusIcon = styled.span`
  margin: 0 10px 0 0;
  font-weight: ${({ theme }) => theme.font.weights.bold};
  font-size: ${({ theme }) => theme.font.sizes.large};
`;

interface DispatchProps {
  loadAssets: (params: LoadAssetsParams) => any;
  loadPackages: (data: LoadPackagePayload) => any;
  loadSalesCount: (params?: LoadReportsCommonParams) => any;
  reactivateAsset: (id: number) => any;
  resetAssetDetails: () => any;
  createItem: (asset: CreateItem) => any;
  openModal: (modalName: ModalNames) => void;
}

interface OwnProps extends RouteComponentProps {}

interface StateProps {
  auth: AuthState;
  assets: ItemsState;
  reports: ReportsState;
  packages: PackagesState;
}

interface State {
  page: number;
  active: boolean;
  reactivateId: number;
  tags: SearchParams;
  activeStep: number;
}

type Props = OwnProps & StateProps & DispatchProps;

interface SearchParams {
  id: Optional<number>;
  title: string;
}

const suggestions: Array<Option> = [
  { value: 'title', displayName: 'Name', tag: 'Name' },
  { value: 'id', displayName: 'ID', tag: 'ID' },
];

const options: Array<Option> = [
  { value: 'true', displayName: 'active', tag: 'active' },
  { value: 'false', displayName: 'deactivated', tag: 'deactivated' },
];

export enum AssetView {
  LIST = 'List View',
  GRID = 'Grid View',
}

const tabs = [
  { name: <MdFormatListBulleted />, tag: 'icon_list' },
  { name: <MdGridOn />, tag: 'icon_grid' },
];

const tabValues = ['list', 'grid'];

export class Assets extends PureComponent<Props, State> {
  state: State = {
    page: 1,
    active: true,
    reactivateId: 0,
    tags: { id: null, title: '' },
    activeStep: 0,
  };

  componentDidMount() {
    const {
      auth: {
        user: { tenant_uuid: tenantUuid },
      },
      assets,
      reports,
      packages,
      resetAssetDetails,
      loadAssets,
      loadPackages,
      loadSalesCount,
      location,
      history,
    } = this.props;
    const { active } = this.state;

    resetAssetDetails();

    if (!assets.isFetching) {
      loadAssets({ page: 1, isActive: active });
    }
    if (!packages.isFetching) {
      loadPackages({ tenantUuid, page: 1, isActive: active });
    }
    if (!reports.isFetching) {
      loadSalesCount();
    }

    const { view } = parse(location.search);

    if (!view || !tabValues.includes(view as string)) {
      history.push('?view=list');
    }
  }

  componentDidUpdate({ location: prevLocation }: Props) {
    const { location, history } = this.props;

    const { view } = parse(location.search);
    const { view: prevView } = parse(prevLocation.search);

    if (prevView !== view && !tabValues.includes(view as string)) {
      history.push('?view=list');
    }
  }

  setActiveStep = (step: number) => {
    this.setState({ activeStep: step });
  };

  renderTableHeaderSection = () => {
    const { active, tags } = this.state;

    return (
      <Container>
        <Cell width={12}>
          <SearchTooltip />
        </Cell>
        <Cell width={10}>
          <Filter options={suggestions} onSearch={this.handleAddition} externalFilters={tags} />
        </Cell>
        <Cell width={2}>
          <ExtendedDropdown
            tag="dropdown_filter"
            fullWidth
            options={options}
            value={active.toString()}
            onChange={this.handleActiveChange}
          />
        </Cell>
      </Container>
    );
  };

  hasRule = (rule: Rules) => {
    const {
      auth: {
        user: { roleRestrictions },
      },
    } = this.props;

    return roleRestrictions.includes(rule);
  };

  handleTabClick = (index: number) => {
    const { history } = this.props;
    const view = tabValues[index];
    history.push(`?view=${view}`);
  };

  displayAssets = () => {
    const { location, assets } = this.props;
    const { page } = this.state;
    const { view } = parse(location.search);
    const AssetsComponent = view === 'list' ? AssetsTable : AssetsGrid;
    const props = {
      assets,
      paginationPage: page,
      handlePageClick: this.handlePageClick,
      handleReactivateAsset: this.handleReactivateAsset,
      renderHeaderSection: this.renderTableHeaderSection,
    };
    return <AssetsComponent {...props} />;
  };

  render() {
    const {
      assets,
      reports: {
        histogram: { allSales },
      },
      packages: { total: secondStatistic },
      createItem,
      location,
    } = this.props;

    const { active, activeStep } = this.state;
    const { firstTotal: firstStatistic } = assets;
    let thirdStatistic = 0;
    if (allSales) {
      thirdStatistic = allSales.total;
    }

    const status = active ? 'active' : 'deactivated';

    const { view } = parse(location.search);

    return (
      <AnalyticsPage tag={view as string} type="page">
        <TourGuideWrapper />
        <TopInformation
          firstStatistic={firstStatistic}
          firstStatisticText={`${status} assets`}
          secondStatistic={secondStatistic}
          secondStatisticText={`${status} packages`}
          thirdStatistic={thirdStatistic}
          thirdStatisticText="total sales"
          isRestricted={this.hasRule(Rules.HIDE_ASSETS_SECTION_TOP_STATS)}
          firstBoxTag="box_active_assets"
          secondBoxTag="box_active_packages"
          thirdBoxTag="box_total_sales"
        />
        <ContentHolder>
          <RestrictedByRule rule={Rules.READONLY_ASSETS_AND_PACKAGES}>
            <CreateAssetButtonDropdown showTypeDialog={this.showTypeDialog} />
          </RestrictedByRule>
          <Container>
            <Cell top={1} width={12}>
              <CustomTabNavigation
                tabs={tabs}
                selectedTabIndex={tabValues.indexOf(view as string)}
                onTabClick={this.handleTabClick}
              />
            </Cell>
            <Cell top={2} width={12}>
              {this.displayAssets()}
            </Cell>
          </Container>
          <BaseModal
            tag="create_a_new_asset_modal"
            onOpen={() => this.setActiveStep(0)}
            title="Create a new asset"
            modalName={ModalNames.CreateAsset}
            render={({ closeModal }: ModalActions) => (
              <AssetCreateWizard
                activeStep={activeStep}
                onStepChange={this.setActiveStep}
                onSubmit={createItem}
                closeModal={closeModal}
              />
            )}
          />
        </ContentHolder>
      </AnalyticsPage>
    );
  }

  private handlePageClick = (page: number) => {
    const { tags, active } = this.state;
    this.setState({
      page,
    });

    const transformedParams = transformSearchParams(tags);

    this.loadAssets(page, transformedParams, active);
  };

  private resetState = () => {
    this.setState({
      page: 1,
      active: true,
      reactivateId: 0,
      tags: { id: 0, title: '' },
      activeStep: 0,
    });
  };

  private handleActiveChange = (active: string) => {
    const { tags } = this.state;
    this.setState({ active: active === 'true', page: 1 });

    const transformedParams = transformSearchParams(tags);

    this.loadAssets(1, transformedParams, active === 'true');
  };

  private loadAssets = (page: number, search: Array<string>, active: boolean) => {
    const { loadAssets } = this.props;
    loadAssets({ page, isActive: active, search });
  };

  private handleReactivateAsset = (id: number) => {
    this.setState({ reactivateId: id });
    const { reactivateAsset } = this.props;
    reactivateAsset(id);
  };

  private showTypeDialog = () => {
    const { openModal } = this.props;
    this.resetState();
    openModal(ModalNames.CreateAsset);
  };

  private handleAddition = (searchParams: SearchParams) => {
    const { active } = this.state;

    const transformedParams = transformSearchParams(searchParams);

    this.loadAssets(1, transformedParams, active);

    this.setState({ tags: searchParams, page: 1 });
  };
}

export const mapStateToProps: MapStateToProps<StateProps, OwnProps, RootState> = (state) => ({
  auth: state.auth,
  assets: state.items,
  reports: state.reports,
  packages: state.packages,
});

const mapDispatchToProps: MapDispatchToProps<DispatchProps, OwnProps> = {
  loadAssets: loadAssetsAction,
  loadPackages: loadPackagesAction,
  loadSalesCount: loadSalesCountAction,
  reactivateAsset: reactivateAssetAction,
  resetAssetDetails: resetAssetDetailsAction,
  createItem: createItemAction,
  openModal: openModalAction,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Assets));
